import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { canSeeCosts } from "../lib/services";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Wallet } from "lucide-react";
import { Button } from "../components/ui/button";
import { formatMoney } from "../lib/currency";
import { CostStat, PeopleTable } from "../components/project/ProjectCostPanel";
import SalaryDialog from "../components/SalaryDialog";
import CostTrend from "../components/project/CostTrend";

function Row({ p }) {
  const [open, setOpen] = useState(false);
  const inr = (p.currency || "USD") === "INR";
  const pct = inr && p.estimated_budget ? Math.round(p.people_cost / p.estimated_budget * 100) : null;
  return (
    <div className="border-b border-[#E4E7EC] last:border-0" data-testid={`cost-row-${p.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full grid grid-cols-12 gap-2 items-center p-3 text-left hover:bg-black/[0.02] text-sm" data-testid={`cost-row-toggle-${p.id}`}>
        <span className="col-span-5 flex items-center gap-2 min-w-0">{open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}<span className="truncate font-medium">{p.project_name}</span><span className="text-[10px] font-mono text-neutral-500 shrink-0">{p.status}</span></span>
        <span className="col-span-2 text-right font-mono">{p.hours}h</span>
        <span className="col-span-2 text-right font-mono font-semibold">{formatMoney(p.people_cost, "INR")}</span>
        <span className="col-span-2 text-right font-mono">{formatMoney(p.estimated_budget || 0, p.currency || "USD")}</span>
        <span className={`col-span-1 text-right font-mono text-xs ${pct > 100 ? "text-rose-600" : pct > 80 ? "text-amber-600" : "text-emerald-700"}`}>{pct === null ? "—" : `${pct}%`}</span>
      </button>
      {open && <div className="bg-[#F8FAFC] px-3 pb-3 space-y-3"><CostTrend months={p.months} title={`${p.project_name} · monthly team cost`} testid={`cost-trend-${p.id}`} /><PeopleTable people={p.people} /><Link to={`/projects/${p.id}?tab=cost`} className="text-xs text-orange-700 ml-3">Open project →</Link></div>}
    </div>
  );
}

export default function ProjectCosts() {
  const { user } = useAuth();
  if (!canSeeCosts(user)) return <Navigate to="/" replace />;
  return <ProjectCostsInner />;
}

function ProjectCostsInner() {
  const [data, setData] = useState(null);
  const [salOpen, setSalOpen] = useState(false);
  const load = () => api.get("/project-costs").then(r => setData(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const ps = data?.projects || [];
  const total = ps.reduce((a, p) => a + p.people_cost, 0);
  const hours = ps.reduce((a, p) => a + p.hours, 0);
  const unpriced = ps.reduce((a, p) => a + p.unpriced_hours, 0);
  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="project-costs-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Finance</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Project Costs</h1>
          <p className="text-neutral-600 text-sm mt-1">What each project has cost in team time: hours logged on tasks × each person's hourly rate.</p>
        </div>
        <Button onClick={() => setSalOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="manage-salaries-btn"><Wallet className="w-4 h-4 mr-1" /> Manage Salaries</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CostStat label="Total team cost" value={formatMoney(total, "INR")} testid="costs-total" />
        <CostStat label="Hours logged" value={Math.round(hours * 100) / 100} testid="costs-hours" />
        <CostStat label="Projects" value={ps.length} testid="costs-projects" />
        <CostStat label="Unpriced hours" value={Math.round(unpriced * 100) / 100} sub={unpriced ? "people with no salary set" : "everyone has a salary"} tone={unpriced ? "text-amber-600" : ""} testid="costs-unpriced" />
      </div>
      <CostTrend months={data?.months || []} title="All projects · monthly team cost" testid="all-cost-trend" />
      <div className="panel overflow-x-auto">
        <div className="min-w-[640px]">
        <div className="grid grid-cols-12 gap-2 p-3 text-[10px] font-mono uppercase text-neutral-500 border-b border-[#E4E7EC]">
          <span className="col-span-5">Project</span><span className="col-span-2 text-right">Hours</span><span className="col-span-2 text-right">Team cost</span><span className="col-span-2 text-right">Budget</span><span className="col-span-1 text-right">Used</span>
        </div>
        {!data && <div className="p-4 text-sm text-neutral-500">Loading…</div>}
        {data && ps.length === 0 && <div className="p-4 text-sm text-neutral-500">No projects yet.</div>}
        {ps.map(p => <Row key={p.id} p={p} />)}
        </div>
      </div>
      <SalaryDialog open={salOpen} onOpenChange={(o) => { setSalOpen(o); if (!o) load(); }} />
    </div>
  );
}
